import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  PlxsButton,
  PlxsCallout,
  PlxsAlert,
  PlxsText,
  PlxsTextLink,
} from "@plexlogic/plexus-ui";
import { DrawData } from "../../types/DrawData";
import DrawWizardConfetti from "../DrawWizardConfetti";
import DrawCountdownAnimation from "../DrawCountdownAnimation";
import WizardSpinner from "../WizardSpinner";
import { DrawSummaryCard, DrawResultsSummary, WinnersTable } from "../drawDetails";
import { useNotification } from "../../contexts/NotificationContext";
import { useRBAC } from "../../hooks/useRBAC";
import { useAuth } from "../../contexts/AuthContext";
import { setWizardSession } from "../../services/wizardStorage";
import config from "../../config";

import {
  apiService,
  DrawResponse,
  SubmitDrawRequest,
} from "../../services/api";

interface ConfirmationStepProps {
  drawData: DrawData;
  drawId?: string;
  currentStep?: number;
  paymentRequired?: boolean;
  onNewDraw?: () => void;
  onDrawExecuted?: (result?: DrawResponse) => void;
  onNext?: () => void;
  prefetchedResult?: DrawResponse | null;
}

const ExecutionConfirmation: React.FC<ConfirmationStepProps> = ({
  drawData,
  drawId,
  currentStep,
  paymentRequired = true,
  onNewDraw,
  onDrawExecuted,
  onNext,
  prefetchedResult,
}) => {
  const { showNotification } = useNotification();
  const { isExternalUser } = useRBAC();
  const { isAuthenticated, loginWithRedirect } = useAuth();
  const [isShuffling, setIsShuffling] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drawResult, setDrawResult] = useState<DrawResponse | null>(
    prefetchedResult ?? null
  );
  const [scheduledRegistered, setScheduledRegistered] = useState(false);

  const downloadCSV = useCallback(async () => {
    if (!drawResult) return;

    try {
      const blob = await apiService.downloadResultsCsv(drawResult.draw_id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${drawResult.draw_name}_results.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Show success toast
      showNotification("Your file was successfully downloaded", "", "positive");
    } catch (error) {
      // Error handling - show error notification
      showNotification(
        "Download failed",
        "Failed to download results CSV. Please try again.",
        "urgent"
      );
    }
  }, [drawResult, showNotification]);

  // Certificate download state
  const [isDownloadingCertificate, setIsDownloadingCertificate] =
    useState(false);
  const [certificateError, setCertificateError] = useState<string | null>(null);

  // Draw execution control
  const [canExecuteDraw, setCanExecuteDraw] = useState(false);
  const [shouldExecuteDraw, setShouldExecuteDraw] = useState(false);
  const [executionTrigger] = useState(0); // Counter to trigger re-execution

  // Ref to track component mount status to prevent memory leaks
  const isMountedRef = useRef(true);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (prefetchedResult) {
      setCanExecuteDraw(false);
      setShouldExecuteDraw(false);
      return;
    }
    setCanExecuteDraw(true);
    setShouldExecuteDraw(true);
  }, [drawData.scrutineerSettings, prefetchedResult]);

  useEffect(() => {
    if (!prefetchedResult) return;
    setShowConfetti(true);
    const t = setTimeout(() => {
      if (isMountedRef.current) setShowConfetti(false);
    }, 5000);
    return () => clearTimeout(t);
  }, [prefetchedResult]);

  // Certificate download function
  const handleDownloadCertificate = async () => {
    if (!isMountedRef.current || !drawResult) return;

    try {
      setIsDownloadingCertificate(true);
      setCertificateError(null); // Clear any existing certificate errors

      // Use the drawId from the result
      const blob = await apiService.downloadCertificate(drawResult.draw_id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `random-draws-${
        drawResult.draw_no || drawResult.draw_id
      }-certificate.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Show success toast
      showNotification("Your file was successfully downloaded", "", "positive");
    } catch (error) {
      // Set user-friendly error message for certificate download only
      const errorMessage =
        error instanceof Error
          ? `Certificate download failed: ${error.message}`
          : "Failed to download certificate. Please try again.";
      setCertificateError(errorMessage);

      // Also show error toast
      showNotification("Download failed", errorMessage, "urgent");
    } finally {
      if (isMountedRef.current) {
        setIsDownloadingCertificate(false);
      }
    }
  };

  // Helper function to handle successful draw completion
  const handleDrawSuccess = useCallback((isScheduled: boolean = false) => {
    if (!isMountedRef.current) return;

    setIsShuffling(false);

    // Only show countdown animation for immediate draws, not scheduled draws
    if (!isScheduled) {
      setShowCountdown(true);
    } else {
      // For scheduled draws, go straight to confetti
      setShowConfetti(true);
      const confettiTimer = setTimeout(() => {
        if (isMountedRef.current) {
          setShowConfetti(false);
        }
      }, 5000);
      return () => clearTimeout(confettiTimer);
    }
  }, []);

  // Handle countdown completion - just hide countdown, wait for API success for confetti
  const handleCountdownComplete = useCallback(() => {
    if (!isMountedRef.current) return;

    setShowCountdown(false);
    // Don't show confetti or call onDrawExecuted yet - wait for API success
  }, []);

  const isScheduledDraw = !!drawData.scheduledDateTime;
  const shouldGateResults =
    config.features.anonymousDraftsEnabled &&
    !isAuthenticated &&
    (!!drawResult || scheduledRegistered);

  const shouldAdvanceToPayment =
    !!drawResult &&
    paymentRequired &&
    drawData.paymentStatus !== "paid" &&
    !drawData.isDemo &&
    isAuthenticated;

  const advanceFiredRef = useRef(false);
  useEffect(() => {
    if (!shouldAdvanceToPayment || !onNext) return;
    if (advanceFiredRef.current) return;
    const t = setTimeout(() => {
      if (!isMountedRef.current) return;
      if (advanceFiredRef.current) return;
      advanceFiredRef.current = true;
      onNext();
    }, 1500);
    return () => clearTimeout(t);
  }, [shouldAdvanceToPayment, onNext]);

  const handleSignUpToViewResults = useCallback(() => {
    setWizardSession(drawData, currentStep ?? 0);
    sessionStorage.setItem("isRedirectingToLogin", "true");
    loginWithRedirect();
  }, [drawData, currentStep, loginWithRedirect]);

  const formatScheduledDateTime = (value: Date | string): string => {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const weekday = d.toLocaleDateString("en-AU", { weekday: "short" });
    const month = d.toLocaleDateString("en-AU", { month: "long" });
    const day = d.getDate();
    const rawHours = d.getHours();
    const minutes = d.getMinutes();
    const ampm = rawHours >= 12 ? "pm" : "am";
    const hours12 = rawHours % 12 || 12;
    const time = `${hours12}:${String(minutes).padStart(2, "0")}${ampm}`;
    return `${weekday} ${day} ${month}, ${time}`;
  };

  const MAX_WINNER_FETCH_ATTEMPTS = 3;
  const WINNER_FETCH_DELAY_MS = 1000;

  const hydrateWinnersIfMissing = useCallback(
    async (response: DrawResponse): Promise<DrawResponse> => {
      const winnersExpected = drawData.prizes.some((p) => p.winners > 0);
      const winnersMissing =
        !response.winners || response.winners.length === 0;

      if (response.status !== "completed" || !winnersMissing || !winnersExpected) {
        return response;
      }

      let latest = response;
      for (let attempt = 0; attempt < MAX_WINNER_FETCH_ATTEMPTS; attempt++) {
        if (!isMountedRef.current) return latest;
        await new Promise((resolve) =>
          setTimeout(resolve, WINNER_FETCH_DELAY_MS)
        );
        if (!isMountedRef.current) return latest;
        try {
          const refreshed = await apiService.getDrawById(response.draw_id);
          latest = refreshed;
          if (refreshed.winners && refreshed.winners.length > 0) {
            return refreshed;
          }
        } catch {
          // Swallow and retry within the bounded loop; render what we have if
          // every attempt fails.
        }
      }
      return latest;
    },
    [drawData.prizes]
  );

  // Retry function for error state
  const retryDraw = useCallback(async () => {
    if (!isMountedRef.current || !drawData.drawId) return;

    try {
      setError(null);
      setShowCountdown(true);

      // Use the dedicated retry endpoint for failed draws
      const response: DrawResponse = await apiService.retryDraw(
        drawData.drawId
      );

      const hydrated = await hydrateWinnersIfMissing(response);

      if (isMountedRef.current) {
        setDrawResult(hydrated);
        setShowCountdown(false);
        setShowConfetti(true);

        // Notify parent that draw has been executed successfully
        if (onDrawExecuted) {
          onDrawExecuted(hydrated);
        }

        // Hide confetti after 5 seconds
        setTimeout(() => {
          if (isMountedRef.current) {
            setShowConfetti(false);
          }
        }, 5000);
      }
    } catch (error) {
      if (isMountedRef.current) {
        setError(
          error instanceof Error
            ? error.message
            : "An error occurred while retrying the draw"
        );
        setShowCountdown(false);
      }
    }
  }, [drawData.drawId, onDrawExecuted, hydrateWinnersIfMissing]);

  useEffect(() => {
    // Only run if validation is complete AND user clicked the execute button
    if (!canExecuteDraw || !shouldExecuteDraw) {
      return;
    }

    let cleanupTimer: (() => void) | null = null;

    const runDraw = async () => {
      // Check if this is a scheduled draw
      const isScheduledDraw = !!drawData.scheduledDateTime;

      if (drawData.isDemo) {
        // Demo mode - show countdown animation immediately
        if (isMountedRef.current) {
          setShowCountdown(true);
        }

        // Demo mode - simulate API processing during countdown (1.5 seconds)
        const drawTimer = setTimeout(() => {
          if (isMountedRef.current) {
            // For scheduled draws, trigger success after simulation
            if (isScheduledDraw) {
              setShowCountdown(false);
              handleDrawSuccess(isScheduledDraw);
            }
            // For immediate draws, countdown continues to completion
          }
        }, 1500);

        cleanupTimer = () => clearTimeout(drawTimer);
      } else if (isScheduledDraw) {
        // Scheduled draw: Update draw with scheduled status and redirect to detail page
        try {
          if (isMountedRef.current) {
            setIsShuffling(true);
          }

          if (!drawData.drawId) {
            throw new Error(
              "Draw ID not found. Please go back to the setup step."
            );
          }

          // Update the draw with scheduled status
          const updateRequest: SubmitDrawRequest = {
            draw_name: drawData.drawName,
            organisation: drawData.organisation,
            entry_method: drawData.entryMethod,
            entry_count: drawData.entryCount,
            prizes: drawData.prizes.map((prize) => ({
              description: prize.description,
              winners: prize.winners,
              reserves: prize.reserves || 0,
              estimated_value: prize.estimatedValue || 0,
            })),
            csv_confirmation_setting: drawData.csvConfirmationSettings
              ? {
                  hasHeader: drawData.csvConfirmationSettings.isHeaderRow,
                  ticketCountColumn:
                    drawData.csvConfirmationSettings.ticketCountColumn,
                  participantIdentificationColumn:
                    drawData.csvConfirmationSettings
                      .participantIdentificationColumn,
                }
              : undefined,
            total_prize_pool_value: drawData.totalPrizePoolValue,
            scrutineer_settings:
              drawData.scrutineerSettings?.scrutineerRequired !== undefined
                ? {
                    scrutineer_required:
                      drawData.scrutineerSettings.scrutineerRequired,
                    scrutineer_name: drawData.scrutineerSettings.scrutineerName,
                    scrutineer_email:
                      drawData.scrutineerSettings.scrutineerEmail,
                    scrutineer_job_title:
                      drawData.scrutineerSettings.scrutineerJobTitle,
                    scrutineer_confirmation:
                      drawData.scrutineerSettings.scrutineerConfirmation,
                  }
                : undefined,
            max_prizes_per_entrant: drawData.maxPrizesPerEntrant,
            scheduled_date_time: drawData.scheduledDateTime?.toISOString(),
            status: "scheduled",
          };

          await apiService.updateDraw(drawData.drawId, updateRequest);

          if (isMountedRef.current) {
            // Notify parent that draw has been scheduled successfully
            if (onDrawExecuted) {
              onDrawExecuted();
            }
            const willGate =
              config.features.anonymousDraftsEnabled && !isAuthenticated;
            if (willGate) {
              setIsShuffling(false);
              setScheduledRegistered(true);
              return;
            }
            // Use microtask queue to ensure state update completes before redirect
            setTimeout(() => {
              window.location.href = `/draws/${drawData.drawId}`;
            }, 0);
          }
        } catch (error) {
          if (isMountedRef.current) {
            setError(
              error instanceof Error
                ? error.message
                : "An error occurred while scheduling the draw"
            );
            setIsShuffling(false);
          }
        }
      } else {
        // Immediate draw: Execute the draw immediately
        try {
          // Show countdown animation while API call is in progress
          if (isMountedRef.current) {
            setShowCountdown(true);
          }

          // Submit the draw using existing drawId (should exist from setup step)
          if (!drawData.drawId) {
            throw new Error(
              "Draw ID not found. Please go back to the setup step."
            );
          }

          // Submit the final draw details
          const submitRequest: SubmitDrawRequest = {
            draw_name: drawData.drawName,
            organisation: drawData.organisation,
            entry_method: drawData.entryMethod,
            entry_count: drawData.entryCount,
            prizes: drawData.prizes.map((prize) => ({
              description: prize.description,
              winners: prize.winners,
              reserves: prize.reserves || 0,
              estimated_value: prize.estimatedValue || 0,
            })),
            csv_confirmation_setting: drawData.csvConfirmationSettings
              ? {
                  hasHeader: drawData.csvConfirmationSettings.isHeaderRow,
                  ticketCountColumn:
                    drawData.csvConfirmationSettings.ticketCountColumn,
                  participantIdentificationColumn:
                    drawData.csvConfirmationSettings
                      .participantIdentificationColumn,
                }
              : undefined,
            total_prize_pool_value: drawData.totalPrizePoolValue,
            scrutineer_settings:
              drawData.scrutineerSettings?.scrutineerRequired !== undefined
                ? {
                    scrutineer_required:
                      drawData.scrutineerSettings.scrutineerRequired,
                    scrutineer_name: drawData.scrutineerSettings.scrutineerName,
                    scrutineer_email:
                      drawData.scrutineerSettings.scrutineerEmail,
                    scrutineer_job_title:
                      drawData.scrutineerSettings.scrutineerJobTitle,
                    scrutineer_confirmation:
                      drawData.scrutineerSettings.scrutineerConfirmation,
                  }
                : undefined,
            max_prizes_per_entrant: drawData.maxPrizesPerEntrant,
          };

          // Start both API call and minimum countdown duration in parallel
          const countdownDuration = 5000; // 5 seconds - match DrawCountdownAnimation duration
          const [response] = await Promise.all([
            apiService.submitDraw(drawData.drawId, submitRequest),
            new Promise((resolve) => setTimeout(resolve, countdownDuration)),
          ]);

          const hydrated = await hydrateWinnersIfMissing(response);

          if (isMountedRef.current) {
            setDrawResult(hydrated);
            setShowCountdown(false);
            setShowConfetti(true);

            // Notify parent that draw has been executed successfully
            if (onDrawExecuted) {
              onDrawExecuted(hydrated);
            }

            // Hide confetti after 5 seconds
            setTimeout(() => {
              if (isMountedRef.current) {
                setShowConfetti(false);
              }
            }, 5000);
          }
        } catch (error) {
          if (isMountedRef.current) {
            // eslint-disable-next-line no-console
            // Error performing draw - handled by notification
            setError(
              error instanceof Error
                ? error.message
                : "An error occurred while performing the draw"
            );
            setShowCountdown(false);
            setIsShuffling(false);
          }
        }
      }
    };

    runDraw();

    // Cleanup function
    return () => {
      if (cleanupTimer) {
        cleanupTimer();
      }
    };
  }, [
    drawData,
    drawId,
    handleDrawSuccess,
    canExecuteDraw,
    shouldExecuteDraw,
    executionTrigger, // Re-run when retry is clicked
    onDrawExecuted,
    isAuthenticated,
    hydrateWinnersIfMissing,
  ]);

  if (error) {
    return (
      <div>
        <div
          style={{
            fontSize: "1.5rem",
            fontWeight: 600,
            marginBottom: "1rem",
            color: "var(--color-text)",
          }}
        >
          Draw failed
        </div>
        {/* <PlxsAlert variant="error">{error}</PlxsAlert> */}
        <div style={{ marginTop: "1rem" }}>
          <PlxsButton
            dataTestId="draw-wizard-retry-button"
            label="Retry draw"
            variant="primary"
            tone="brand"
            onClick={retryDraw}
          />
        </div>
      </div>
    );
  }

  // Show loading spinner only for scheduled draws
  // For immediate draws: countdown animation plays during API call instead
  if (isShuffling) {
    return (
      <div style={{ textAlign: "center", padding: "3rem 0" }}>
        <WizardSpinner size={40} dataTestId="draw-scheduling-spinner" />
        <PlxsText size="sm">Scheduling your draw...</PlxsText>
        <div style={{ marginTop: "1rem" }}></div>
      </div>
    );
  }

  if (shouldAdvanceToPayment) {
    return (
      <div style={{ textAlign: "center", padding: "3rem 0" }}>
        <div
          style={{
            color: "var(--plexus-color-positive-400)",
            fontWeight: 700,
            fontSize: "2rem",
            marginBottom: "var(--plexus-space-md)",
          }}
        >
          Draw complete!
        </div>
        <PlxsText size="sm">
          Taking you to payment to reveal your winners...
        </PlxsText>
        <div style={{ marginTop: "var(--plexus-space-lg)" }}>
          <WizardSpinner size={32} dataTestId="advancing-to-payment-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      {/* Countdown animation overlay */}
      {showCountdown && (
        <DrawCountdownAnimation
          onComplete={handleCountdownComplete}
          duration={5000}
        />
      )}

      {showConfetti && <DrawWizardConfetti />}

      <div
        data-testid={
          shouldGateResults ? "results-gated-wrapper" : undefined
        }
        style={
          shouldGateResults
            ? {
                position: "relative",
                filter: "blur(8px)",
                pointerEvents: "none",
                userSelect: "none",
              }
            : undefined
        }
        aria-hidden={shouldGateResults || undefined}
      >
      {/* Only show success message after draw result is available */}
      {drawResult && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            marginBottom: "var(--plexus-space-xl)", // 32px consistent spacing
          }}
        >
          <div
            style={{
              color: "var(--plexus-color-positive-400)",
              fontWeight: 700,
              fontSize: "2.25rem", // Large font size for emphasis
              margin: 0,
              lineHeight: 1.2,
              marginBottom: "var(--plexus-space-md)", // 16px spacing
            }}
          >
            Draw complete!
          </div>
          <div
            style={{
              fontSize: "1.125rem",
              color: "var(--plexus-color-neutral-100)", // Better body text readability
              lineHeight: 1.4,
            }}
          >
            Done – your results are ready.
            {drawData.isDemo && (
              <span style={{ color: "var(--plexus-color-neutral-100)" }}>
                {" "}
                (Demo mode)
              </span>
            )}
          </div>
        </div>
      )}

      {/* Draw details section - using reusable component */}
      <DrawSummaryCard
        drawNo={drawData.drawNo}
        prizes={drawData.prizes}
        status={drawResult?.status}
      />

      {/* Results Summary - using reusable component */}
      {drawResult && (
        <DrawResultsSummary
          totalWinners={drawData.prizes.reduce(
            (sum, prize) => sum + prize.winners,
            0
          )}
          totalReserves={drawData.prizes.reduce(
            (sum, prize) => sum + prize.reserves,
            0
          )}
          totalEntries={drawData.entryCount}
        />
      )}

      {drawResult && drawResult.winners && drawResult.winners.length > 0 && (
        <WinnersTable
          winners={drawResult.winners}
          onDownloadResults={downloadCSV}
          entryMethod={drawData.entryMethod}
        />
      )}

      {/* Scrutineer Details Display (if applicable) */}
      {drawData.scrutineerSettings?.scrutineerRequired && (
        <div
          style={{
            backgroundColor: "var(--plexus-color-surface-50)",
            borderRadius: "var(--plexus-space-xs)",
            padding: "var(--plexus-space-xl)",
            marginBottom: "var(--plexus-space-xl)",
            border: `var(--plexus-border-width-thin) solid var(--plexus-color-neutral-600)`,
          }}
        >
          <h3
            style={{
              margin: 0,
              marginBottom: "var(--plexus-space-md)",
              fontSize: "1.125rem",
              fontWeight: 600,
              color: "var(--plexus-color-neutral-100)",
            }}
          >
            Scrutineer details
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "160px 1fr",
              gap: "var(--plexus-space-sm)",
              fontSize: "0.875rem",
            }}
          >
            <div style={{ color: "var(--plexus-color-neutral-300)" }}>
              <PlxsText size="sm">Scrutineer name:</PlxsText>
            </div>
            <PlxsText size="sm">
              {drawData.scrutineerSettings.scrutineerName || "N/A"}
            </PlxsText>

            <div style={{ color: "var(--plexus-color-neutral-300)" }}>
              <PlxsText size="sm">Scrutineer email:</PlxsText>
            </div>
            <PlxsText size="sm">
              {drawData.scrutineerSettings.scrutineerEmail || "N/A"}
            </PlxsText>

            <div style={{ color: "var(--plexus-color-neutral-300)" }}>
              <PlxsText size="sm">Scrutineer job title:</PlxsText>
            </div>
            <PlxsText size="sm">
              {drawData.scrutineerSettings.scrutineerJobTitle || "N/A"}
            </PlxsText>

            <div style={{ color: "var(--plexus-color-neutral-300)" }}>
              <PlxsText size="sm">Confirmation:</PlxsText>
            </div>
            <PlxsText size="sm">
              {drawData.scrutineerSettings.scrutineerConfirmation
                ? "Scrutineer presence confirmed"
                : "N/A"}
            </PlxsText>
          </div>
        </div>
      )}

      {/* Certificate download error display */}
      {certificateError && (
        <div style={{ marginBottom: "var(--plexus-space-lg)" }}>
          <PlxsAlert variant="error">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>{certificateError}</span>
              <div style={{ marginLeft: "var(--plexus-space-sm)" }}>
                <PlxsButton
                  dataTestId="dismiss-certificate-error"
                  label="Dismiss"
                  variant="secondary"
                  size="sm"
                  tone="neutral"
                  onClick={() => setCertificateError(null)}
                />
              </div>
            </div>
          </PlxsAlert>
        </div>
      )}

      <div
        data-testid="run-another-nudge"
        style={{
          display: "flex",
          justifyContent: "center",
          textAlign: "center",
          marginTop: "var(--plexus-space-xl)",
          marginBottom: "var(--plexus-space-md)",
          color: "var(--plexus-color-neutral-300)",
        }}
      >
        <PlxsText size="sm">
          Download your results, or run another – it's just as quick.
        </PlxsText>
      </div>

      {/* Action buttons with proper tones */}
      <div
        style={{
          display: "flex",
          gap: "var(--plexus-space-lg)", // 24px consistent button spacing
          justifyContent: "center",
          flexWrap: "wrap",
          marginBottom: "var(--plexus-space-xl)", // 32px consistent spacing
        }}
      >
        <PlxsButton
          dataTestId="draw-wizard-download-certificate-button"
          label={
            isDownloadingCertificate ? "Downloading..." : "Download certificate"
          }
          variant="primary"
          tone="positive"
          onClick={handleDownloadCertificate}
          isDisabled={isDownloadingCertificate}
        />
        <PlxsButton
          dataTestId="draw-wizard-download-results-button"
          label="Download results (CSV)"
          variant="secondary"
          tone="neutral"
          onClick={downloadCSV}
          isDisabled={!drawResult}
        />
        <PlxsButton
          dataTestId="draw-wizard-new-draw-button"
          label="Start new draw"
          variant="secondary"
          tone="neutral"
          onClick={onNewDraw}
        />
      </div>

      {/* Deep link and draw details access */}
      <div
        style={{
          backgroundColor: "var(--plexus-color-surface-50)",
          borderRadius: "var(--plexus-space-xs)",
          padding: "var(--plexus-space-xl)",
          marginBottom: "var(--plexus-space-xl)",
          textAlign: "center",
          minHeight: "180px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {!drawResult ? (
          // Loading state - show spinner while draw is being submitted
          <div
            style={{
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              gap: "var(--plexus-space-sm)",
              alignItems: "center",
            }}
          >
            <WizardSpinner size={40} dataTestId="draw-finalizing-spinner" />
            <PlxsText>Finalizing draw quick access...</PlxsText>
          </div>
        ) : (
          // Draw result available - show quick access buttons
          <>
            <div
              style={{
                fontWeight: 600,
                color: "var(--plexus-color-positive-400)",
                marginBottom: "var(--plexus-space-sm)",
                fontSize: "1rem",
              }}
            >
              Quick access
            </div>
            <div
              style={{
                color: "var(--plexus-color-neutral-300)",
                marginBottom: "var(--plexus-space-lg)",
                fontSize: "0.875rem",
              }}
            >
              Access your draw anytime from your account, or use the link below
              for direct access.
            </div>
            <div
              style={{
                display: "flex",
                gap: "var(--plexus-space-md)",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <PlxsButton
                dataTestId="draw-wizard-copy-link-button"
                label="Copy draw link"
                variant="secondary"
                tone="positive"
                onClick={() => {
                  const drawUrl = `${window.location.origin}/draws/${drawResult.draw_id}`;
                  navigator.clipboard.writeText(drawUrl);
                  showNotification(
                    "Draw link copied to clipboard",
                    "",
                    "positive"
                  );
                }}
              />
              <PlxsButton
                dataTestId="draw-wizard-view-details-button"
                label="View draw details"
                variant="primary"
                tone="positive"
                onClick={() => {
                  window.open(`/draws/${drawResult.draw_id}`, "_blank");
                }}
              />
            </div>
          </>
        )}
      </div>

      {/* Cross-sell section - Only show to external users */}
      {isExternalUser && (
        <div
          style={{
            backgroundColor: "var(--plexus-color-surface-50)",
            borderRadius: "var(--plexus-space-xs)", // 4px
            padding: "var(--plexus-space-xl)", // 32px consistent padding
            border: `var(--plexus-border-width-thin) solid var(--plexus-color-neutral-600)`, // Subtle light border
            marginBottom: "var(--plexus-space-xl)", // 32px consistent spacing
          }}
        >
          {/* Cross-sell heading */}
          <div
            style={{
              marginBottom: "var(--plexus-space-xl)",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: "1.5rem", // Slightly larger for better hierarchy
                fontWeight: 600,
                marginBottom: "var(--plexus-space-lg)", // 24px spacing
                color: "var(--plexus-color-neutral-100)", // Dark text for readability
                lineHeight: 1.3,
              }}
            >
              Get expert-reviewed, compliant T&Cs in under 24 hours for your
              next promotion.
            </div>

            {/* Get Started Button */}
            <div
              style={{
                marginBottom: "var(--plexus-space-lg)",
              }}
            >
              <PlxsButton
                dataTestId="cross-sell-get-started"
                label="Get started"
                variant="primary"
                tone="brand"
                onClick={() => {
                  window.open(
                    "https://www.plexus.co/promotion-wizard-get-started",
                    "_blank"
                  );
                }}
              />
            </div>

            {/* Rating line */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "var(--plexus-space-sm)", // 8px consistent spacing
              }}
            >
              <PlxsText size="sm">4.8</PlxsText>
              <div style={{ display: "flex", gap: "2px" }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    style={{
                      color: "var(--plexus-color-caution-400)", // Use Plexus caution token for star rating
                      fontSize: "1rem",
                    }}
                  >
                    ★
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      </div>

      {shouldGateResults && (
        <div
          data-testid="results-signup-overlay"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "var(--plexus-space-lg)",
            zIndex: 5,
          }}
        >
          <div
            style={{
              backgroundColor: "var(--plexus-color-surface, #fff)",
              borderRadius: "var(--plexus-space-sm)",
              padding: "var(--plexus-space-xl)",
              maxWidth: 480,
              textAlign: "center",
              boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
              border: `var(--plexus-border-width-thin) solid var(--plexus-color-neutral-600)`,
            }}
          >
            <div
              style={{
                fontSize: "1.5rem",
                fontWeight: 600,
                marginBottom: "var(--plexus-space-md)",
                color: "var(--plexus-color-neutral-100)",
                lineHeight: 1.3,
              }}
              data-testid="results-signup-heading"
            >
              {isScheduledDraw
                ? `Your draw runs on ${formatScheduledDateTime(
                    drawData.scheduledDateTime as Date
                  )}.`
                : "Your draw has successfully run!"}
            </div>
            <div
              style={{
                marginBottom: "var(--plexus-space-lg)",
                color: "var(--plexus-color-neutral-200)",
              }}
            >
              <PlxsText size="sm">
                {isScheduledDraw ? (
                  "Create a free account to follow your draw. Once it runs, you can purchase your results – winners plus an audit-ready certificate."
                ) : (
                  <>
                    Create a free{" "}
                    <PlxsTextLink href="https://www.plexus.co" target="_blank">
                      Plexus
                    </PlxsTextLink>{" "}
                    account to view and save your results.
                  </>
                )}
              </PlxsText>
            </div>
            <PlxsButton
              dataTestId="results-signup-cta"
              label="Login or create my account"
              variant="primary"
              tone="brand"
              onClick={handleSignUpToViewResults}
            />
          </div>
        </div>
      )}

      {drawData.isDemo && (
        <div
          style={{ marginTop: "var(--plexus-space-xl)", textAlign: "center" }}
        >
          {" "}
          {/* 32px consistent spacing */}
          <PlxsCallout
            dataTestId="confirmation-demo-callout"
            tone="brand"
            message="Demo mode: This is a demonstration. No actual random draws were performed and no certificates were generated."
          />
        </div>
      )}
    </div>
  );
};

export default ExecutionConfirmation;
